import React, { useEffect, useState } from 'react'
import { Row, Col, Image, Typography, Space, Badge, Tooltip } from 'antd'
import { ProductOptions } from 'Store/product/types'
import styled from 'styled-components'
import theme, { MyTheme } from 'Components/common/Theme'
import { themeConfig } from 'config'
import { AppInput } from 'Components/form'
import { decimalCount, formValidation, isFormError, isFormRequired, numberWithComma } from 'helpers/format'
import RootState from 'typings/RootState'
import { selectDatas } from 'Store/config/selector'
import { connect } from 'react-redux'
import { selectLatestGrnList } from 'Store/grn/selector'
import { LatestGrnData } from 'Store/grn/types'
import { AlertInfo } from 'Components/alert'
import { InfoCircleOutlined, InfoCircleTwoTone } from '@ant-design/icons'
import { cloneDeep } from 'lodash'

type Props = {
  onUpdate: (data: ProductOptions) => void
  token?: string
  isPackageC?: boolean
  data: ProductOptions
  productID: number
} & ReduxProps

const ProductOptionsEditPrice: React.FunctionComponent<Props> = props => {
  const { isPackageC = false, configs, onUpdate, data, latestGrnList, productID } = props
  const priceLabel = configs?.value.price_labels || []
  const enableProductOnline = themeConfig?.component?.elementOnlineProduct
  const [formData, setFormData] = useState(data)
  const [formError, setFormError] = useState([] as string[])
  const requireFeild = ['title', 'price', 'weight', 'offline_price']

  const validateForm = () => {
    const error = formValidation(requireFeild, formData)

    setFormError([...error])
    return error.length > 0 ? false : true
  }

  useEffect(() => {
    validateForm()
  }, [formData])

  useEffect(() => {
    if (data) {
      const form = {
        ...data,
      } as ProductOptions
      setFormData({ ...form })
    }
    return () => {
      setFormError([])
    }
  }, [data])

  const getConfigName = (key: string): string => {
    const result = priceLabel && priceLabel.find(item => item.name === key)
    return `${result?.value || ''} ${result?.description ? '(' + result?.description + ')' : ''}`
  }

  const isRequired = (name: string) => {
    return isFormRequired(requireFeild, name)
  }

  const isError = (name: string) => {
    return isFormError(formError, name)
  }

  const onUpdatePrice = (val: any) => {
    const newVal = cloneDeep(val)
    const count = decimalCount(val)
    if (count > 2) return

    setFormData({
      ...formData,
      price: newVal,
      offline_price: newVal,
    })

    onUpdate({
      ...formData,
      price: newVal,
      offline_price: newVal,
    })
  }

  const onUpdateData = (val: any, key: string) => {
    let newVal = cloneDeep(val)
    if (
      key === 'price' ||
      key === 'offline_price' ||
      key === 'price_a' ||
      key === 'price_b' ||
      key === 'price_c'
    ) {
      const count = decimalCount(val)
      if (count > 2) return
    }

    if (key === 'online_percentage') {
      newVal = parseInt(val)
      if (newVal > 100) {
        newVal = 100
      }
    }

    setFormData({
      ...formData,
      [key]: newVal,
    })

    onUpdate({
      ...formData,
      [key]: newVal,
    })
  }

  const description = (latestGrn: LatestGrnData) => {
    return (
      <Row gutter={[8, 8]}>
        <Col md={14} xs={24}>
          <Space direction="vertical" size="small">
            <Badge
              color={theme.colors.primary}
              text={`ราคาต้นทุน/ต่อหน่วย ที่รับเข้าล่าสุด = ${numberWithComma(
                latestGrn?.grn_item?.cost,
                true,
              )} บาท`}
            />
            <Badge
              color={theme.colors.primary}
              text={`ต้นทุนแฝง ที่รับเข้าล่าสุด = ${numberWithComma(
                latestGrn?.grn_item?.hidden_cost,
                true,
              )} บาท`}
              //text="ต้นทุนแฝง ที่รับเข้าล่าสุด = 1,050.00 บาท"
            />
          </Space>
        </Col>
        <Col md={10} xs={24}>
          <Space direction="vertical" size="small">
            <Badge
              color={theme.colors.primary}
              text={`ราคาต้นทุน = ${numberWithComma(latestGrn?.grn_item?.average_cost, true)} บาท`}
            />
            <Badge
              color={theme.colors.primary}
              text={`ต้นทุนเฉลี่ย = ${numberWithComma(latestGrn?.total_average_cost, true)} บาท`}
            />
          </Space>
        </Col>
      </Row>
    )
  }

  const getGrnIcon = (props: any) => {
    const { onExpand, record, expanded } = props
    return expanded ? (
      <TypographyTextGrey onClick={e => onExpand(record, e)}>ซ่อนต้นทุน</TypographyTextGrey>
    ) : (
      <TypographyTextGrey onClick={e => onExpand(record, e)}>แสดงต้นทุน</TypographyTextGrey>
    )
  }

  const getGrnInfo = (record: ProductOptions) => {
    const latestGrn =
      latestGrnList &&
      latestGrnList?.find(item => item.product_id === productID && item.product_option_id === record.id)
    return (
      <Description>
        <AlertInfo
          message={
            latestGrn
              ? `ข้อมูลต้นทุนและราคาเฉลี่ยของสินค้าที่รับเข้าคลังล่าสุด`
              : 'ไม่พบข้อมูล ต้นทุนและราคาเฉลี่ยของสินค้าที่รับเข้าคลังล่าสุด'
          }
          description={latestGrn ? description(latestGrn) : ''}
          banner
        />
      </Description>
    )
  }

  const renderTooltip = (title: any) => {
    return (
      <StockWrapper>
        <Tooltip title={title} placement="right">
          {' '}
          <InfoCircleOutlined />
        </Tooltip>
      </StockWrapper>
    )
  }

  const RequireData = (
    <Row gutter={[8, 8]}>
      <Col md={24} xs={24}>
        <Title> {data.title}</Title>
      </Col>
      <Col md={24} xs={24}>
        {getGrnInfo(data)}
      </Col>
      <Col md={24} xs={24}>
        <Row justify="space-between" gutter={[8, 8]}>
          {enableProductOnline && (
            <Col md={12} xs={24}>
              <AppInput
                name="price"
                label="ราคาขายออนไลน์"
                type="number"
                isRequired={isRequired('price')}
                isError={isError('price')}
                value={formData.price}
                onChange={onUpdateData}
                resProps={{ suffix: 'บาท' }}
                labelIcon={renderTooltip(
                  <TextRemark>
                    ราคาขายออนไลน์ คือ ราคาสินค้าที่ขายบนระบบออนไลน์ (ขายผ่าน App/Web{' '}
                    {themeConfig.title.appName})
                  </TextRemark>,
                )}
              />
            </Col>
          )}
          {enableProductOnline && (
            <Col md={12} xs={24}>
              <AppInput
                name="offline_price"
                label="ราคาขายหน้าร้าน"
                isRequired={isRequired('offline_price')}
                isError={isError('offline_price')}
                value={formData.offline_price}
                onChange={onUpdateData}
                type="number"
                resProps={{ suffix: 'บาท' }}
                labelIcon={renderTooltip(
                  <TextRemark>ราคาขายหน้าร้าน คือ ราคาสินค้าที่ขายผ่านระบบ Terminal</TextRemark>,
                )}
              />
            </Col>
          )}
          {!enableProductOnline && (
            <Col md={12} xs={24}>
              <AppInput
                name="offline_price"
                label="ราคาขายหน้าร้าน"
                isRequired={isRequired('offline_price')}
                isError={isError('offline_price')}
                value={formData.offline_price}
                onChange={onUpdatePrice}
                type="number"
                resProps={{ suffix: 'บาท' }}
                labelIcon={renderTooltip(
                  <TextRemark>ราคาขายหน้าร้าน คือ ราคาสินค้าที่ขายผ่านระบบ Terminal</TextRemark>,
                )}
              />
            </Col>
          )}
        </Row>
      </Col>
      <Col md={12} xs={24}>
        <AppInput
          name="price_a"
          label={getConfigName('price_a')}
          isRequired={isRequired('price_a')}
          isError={isError('price_a')}
          value={formData.price_a}
          onChange={onUpdateData}
          type="number"
          resProps={{ suffix: 'บาท' }}
        />
      </Col>
      <Col md={12} xs={24}>
        <AppInput
          name="price_b"
          label={getConfigName('price_b')}
          isRequired={isRequired('price_b')}
          isError={isError('price_b')}
          value={formData.price_b}
          onChange={onUpdateData}
          type="number"
          resProps={{ suffix: 'บาท' }}
        />
      </Col>
      <Col md={12} xs={24}>
        <AppInput
          name="price_c"
          label={getConfigName('price_c')}
          isRequired={isRequired('price_c')}
          isError={isError('price_c')}
          value={formData.price_c}
          onChange={onUpdateData}
          type="number"
          resProps={{ suffix: 'บาท' }}
        />
      </Col>
    </Row>
  )

  const DefaultData = (
    <Row gutter={[8, 8]}>
      <Col md={24} xs={24}>
        <Title> {data.title}</Title>
      </Col>
      <Col md={12} xs={24}>
        <AppInput
          name="price"
          label="ราคาขาย"
          type="number"
          isRequired={isRequired('price')}
          isError={isError('price')}
          value={formData.price}
          onChange={onUpdateData}
          resProps={{ suffix: 'บาท' }}
        />
      </Col>
    </Row>
  )

  return (
    <MyRow gutter={[16, 16]}>
      <Col md={24} xs={24}>
        {isPackageC ? RequireData : DefaultData}
      </Col>
    </MyRow>
  )
}

const MyRow = styled(Row)``

const TypographyTextGrey = styled(Typography.Link)`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.grey} !important;
`

const StockWrapper = styled.span`
  padding: 0 2px;
`

const TextRemark = styled.span`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
`

const Title = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body2};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.medium};
  margin-bottom: 10px;
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.primary};
`

const Description = styled.div`
  .ant-descriptions-title {
    font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  }
  .ant-alert-message {
    font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body3};
  }
  padding: 5px 0;
  margin-bottom: 10px;
  th,
  td,
  span {
    font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  }
`

const mapStateToProps = (state: RootState) => {
  return {
    configs: selectDatas(state),
    latestGrnList: selectLatestGrnList(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(ProductOptionsEditPrice)
